/**
 * GuardianDetailsStep - Step 4: Guardian and contact details
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { UserPlus, Mail, Phone, ArrowRight, ArrowLeft } from 'lucide-react';

export const GuardianDetailsStep = ({ guardianForm, setGuardianForm, onNext, onBack, submitting }) => {
  const handleNext = () => {
    if (!guardianForm.email || !guardianForm.first_name || !guardianForm.last_name) {
      toast.error('Please fill in all required guardian fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guardianForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    onNext();
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#FF007F]" />
          Guardian & Contact Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-white/70">First Name *</Label>
            <Input
              value={guardianForm.first_name}
              onChange={(e) => setGuardianForm({ ...guardianForm, first_name: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="First name"
              data-testid="guardian-first-name"
            />
          </div>
          <div>
            <Label className="text-white/70">Last Name *</Label>
            <Input
              value={guardianForm.last_name}
              onChange={(e) => setGuardianForm({ ...guardianForm, last_name: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Last name"
              data-testid="guardian-last-name"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-white/70">Email Address *</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="email"
              value={guardianForm.email}
              onChange={(e) => setGuardianForm({ ...guardianForm, email: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 text-white"
              placeholder="email@example.com"
              data-testid="guardian-email"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-white/70">Phone Number *</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="tel"
              value={guardianForm.phone}
              onChange={(e) => setGuardianForm({ ...guardianForm, phone: e.target.value })}
              className="pl-10 bg-white/5 border-white/10 text-white"
              placeholder="07xxx xxxxxx"
              data-testid="guardian-phone"
            />
          </div>
        </div>

        {/* Second Guardian (Optional) */}
        <div className="pt-4 border-t border-white/10">
          <p className="text-sm font-medium text-white/70 mb-3">Second Guardian (Optional)</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              value={guardianForm.guardian2_first_name}
              onChange={(e) => setGuardianForm({ ...guardianForm, guardian2_first_name: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="First name"
            />
            <Input
              value={guardianForm.guardian2_last_name}
              onChange={(e) => setGuardianForm({ ...guardianForm, guardian2_last_name: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Last name"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <Input
              type="email"
              value={guardianForm.guardian2_email}
              onChange={(e) => setGuardianForm({ ...guardianForm, guardian2_email: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Email (optional)"
            />
            <Input
              type="tel"
              value={guardianForm.guardian2_phone}
              onChange={(e) => setGuardianForm({ ...guardianForm, guardian2_phone: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Phone (optional)"
            />
          </div>
          <div className="flex items-center gap-3 mt-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <Checkbox
              id="guardian2_receives_emails"
              checked={guardianForm.guardian2_receives_emails}
              onCheckedChange={(checked) => setGuardianForm({ ...guardianForm, guardian2_receives_emails: checked })}
              className="border-white/30 data-[state=checked]:bg-blue-500"
            />
            <label htmlFor="guardian2_receives_emails" className="text-white/90 text-sm cursor-pointer">
              Send all school communications to both guardians
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleNext}
            disabled={submitting}
            className="flex-1 bg-[#FF007F] hover:bg-[#D6006A] text-white"
            data-testid="step4-continue"
          >
            {submitting ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GuardianDetailsStep;
